/**
 * Pure mapping between the invite flow's wire shape (camelCase keys, lowercase role strings)
 * and the VisionMembership proto messages (enum-typed). Kept pure and isolated so it is
 * independently testable and the accept page stays unaware of the transport's enum typing.
 *
 * rrecce accounts have no username, so the proto invite view carries no `invitedByUsername`;
 * it is intentionally absent from the mapped shape.
 */

import { VisionMemberRole } from '../gen/vision/membership/v1/vision_membership_pb';

const ROLE_FROM_PROTO = {
  [VisionMemberRole.VIEW_ONLY]: 'view_only',
  [VisionMemberRole.EDIT]: 'edit',
  [VisionMemberRole.FULL_ACCESS]: 'full_access',
};

const ROLE_TO_PROTO = {
  view_only: VisionMemberRole.VIEW_ONLY,
  edit: VisionMemberRole.EDIT,
  full_access: VisionMemberRole.FULL_ACCESS,
};

function lookup(map, key, label) {
  const mapped = map[key];
  if (mapped === undefined) {
    throw new Error(`Unmappable ${label}: ${key}`);
  }
  return mapped;
}

export function roleFromProto(role) {
  return lookup(ROLE_FROM_PROTO, role, 'role');
}

export function roleToProto(role) {
  return lookup(ROLE_TO_PROTO, role, 'role');
}

export function inviteFromProto(invite) {
  return {
    email: invite.email,
    projectName: invite.projectName,
    invitedByName: invite.invitedByName,
    role: roleFromProto(invite.role),
  };
}

export function acceptResultFromProto(result) {
  return {
    projectId: result.projectId,
    projectName: result.projectName,
    alreadyMember: result.alreadyMember,
  };
}

/**
 * Maps a `VisionInviteView` proto (from ListVisionInvites or the realtime invite-created event)
 * to the pending-invite shape the team UI reads: `id` keys the list, `email` is displayed.
 */
export function inviteViewFromProto(invite) {
  return {
    id: invite.inviteId,
    email: invite.email,
    role: roleFromProto(invite.role),
    invited_by_name: invite.invitedByName,
  };
}

/**
 * Maps a `VisionMemberView` proto (from ListVisionMembers or the realtime member-joined event)
 * to the member shape the team UI reads. The owner row arrives with `isOwner = true`, mapped to
 * `is_creator`. rrecce accounts have no username, so no `username` is set (the UI's `@username`
 * line is conditional and simply does not render); `pfp_url` is empty when the account has no
 * picture, which the avatar falls back to initials for.
 */
export function memberViewFromProto(member) {
  return {
    user_id: member.accountId,
    display_name: member.name,
    avatar_path: member.pfpUrl || null,
    role: roleFromProto(member.role),
    is_creator: member.isOwner,
    is_me: member.isMe,
  };
}
